import { DropdownMenuItem } from '@/components/ui/DropdownMenu'
import {
  DocumentMagnifyingGlassIcon,
  PlusCircleIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid'
import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
// import { Separator } from '@radix-ui/react-separator'
import { Button, ButtonText } from '@/components/ui/button'
import { formatDate, isAfter, isBefore, startOfDay } from 'date-fns'

// import MoneyPathsTable from './MoneyPathsTable'
import {
  Link,
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import useCloseToOrigin from '@/hooks/useCloseToOrigin'
import TransactionListTable from './TransactionListTable'
import { AnimatePresence, motion } from 'framer-motion'
import { Separator } from '../ui/separator'
import useGetPeriodByOffset from '@/services/api/cache/useGetPeriodByOffset'
import useGetVault from '@/services/api/vault/useGetVault'
import useGetTransactionCategory from '@/services/api/transaction-category/useGetTransactionCategory'
import { Skeleton } from '../ui/skeleton'
import useVaultBalanceForOffset from '@/services/api/cash-flow/useVaultBalanceForOffset'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import { Label } from '../ui/Label'
import { Portal } from '@radix-ui/react-portal'
import { PanelBottom } from 'lucide-react'
import useAppStore from '@/store/useAppStore'
import useDeviceClass from '@/mobile/useDeviceClass'
import MobileTransactionList from '@/mobile/MobileTransactionList'

// Phone and tablet show the list inside the mobile route sheet instead of the
// docked panel + right-hand form (see src/mobile/MobileLayoutHome).
export default function TransactionListModal(props: {
  showRightPanel: boolean
}) {
  const device = useDeviceClass()
  if (device !== 'desktop') return <MobileTransactionList />
  return <DesktopTransactionListModal {...props} />
}

function DesktopTransactionListModal({
  showRightPanel,
}: {
  showRightPanel: boolean
}) {
  const dockTransactionListView = useAppStore(
    (state) => state.dockTransactionListView,
  )
  const setDockTransactionListView = useAppStore(
    (state) => state.setDockTransactionListView,
  )

  //   const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const {
    categoryId,
    vaultId,
    transactionId,
    modelId: forModelId,
  } = useParams()

  const [searchParams] = useSearchParams()
  const offset = searchParams.get('offset')
  const navigate = useNavigate()

  const closeToOrigin = useCloseToOrigin()
  const formatter = useNumberFormatter({
    // style: 'currency',
    // currency: 'AUD',
    // locale: 'ja-JP',
  })

  const {
    data: vaultEntity,
    status: vaultEntityStatus,
    isLoading: vaultEntityIsLoading,
  } = useGetVault({
    vaultId,
  })

  const {
    data: categoryEntity,
    isLoading: isLoadingCategory,
    refetch: refetchTransactionCategory,
  } = useGetTransactionCategory({ id: categoryId })

  const {
    data: period,
    refetch: refetchPeriod,
    isLoading: periodIsLoading,
  } = useGetPeriodByOffset({
    offset,
  })

  const { data: vaultBalances, isLoading: balancesIsLoading } =
    useVaultBalanceForOffset({
      offset: offset !== null ? Number(offset) : undefined,
      vaultId: vaultId ? Number(vaultId) : undefined,
    })

  const vaultOpeningBalance = vaultBalances?.opening_balance
  const vaultClosingBalance = vaultBalances?.closing_balance
  const openingBalanceIsLoading = balancesIsLoading
  const closingBalanceIsLoading = balancesIsLoading

  useEffect(() => {
    setOpen(true)
  }, [])

  const isLoading =
    isLoadingCategory ||
    vaultEntityIsLoading ||
    periodIsLoading ||
    closingBalanceIsLoading ||
    openingBalanceIsLoading

  const title = vaultEntity
    ? `Vault: ${vaultEntity?.name}`
    : categoryEntity
      ? `Category: ${categoryEntity?.name}`
      : null

  function retrieveSearchParams(type: string) {
    const p = new URLSearchParams(searchParams)
    p.set('type', type)
    return p.toString()
  }

  function handleDock() {
    setDockTransactionListView(!dockTransactionListView)

    navigate(
      {
        // pathname: `/transaction-list/${vaultId ? `vaults/${vaultId}` : categoryId ? `categories/${categoryId}` : 'undefined'}/transactions${close ? '' : '/new'}`,
        search: searchParams.toString(),
      },
      { replace: true },
    )
  }

  //   if (isLoading || !period) return <Skeleton className="h-9 w-full" />

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        defaultOpen={true}
        modal={false}
      >
        <DialogContent
          containerId="new-container"
          customOverlay={!dockTransactionListView}
          //   onInteractOutside={(e) => {
          //     e.preventDefault()
          //   }}
          onPointerDownOutside={(e) => {
            e.preventDefault()
          }}
          onFocusOutside={(e) => {
            e.preventDefault()
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
          }}
          autoFocus={false}
          absolute
          className={`min-h-[15rem] md:max-w-5xl ${dockTransactionListView ? 'min-h-[465px]--- relative bottom-0 left-0 top-[unset] max-h-[48vh] min-h-full min-w-full translate-x-0 translate-y-0 transform-none' : 'max-h-[80vh]'}`}
        >
          <DialogHeader className="kiruba items-start">
            <DialogTitle className="" asChild>
              {isLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <div className="flex w-full flex-col gap-4">
                  <p>{title}</p>
                  <div className="flex w-full justify-between">
                    <div className="flex w-fit  gap-2">
                      <div className="flex items-center justify-center rounded-full bg-gray-tr-12  px-1.5 py-[0.125rem] text-xs text-txt-primary group-data-[state=thisweek]:bg-accent-tr-24 group-data-[state=thisweek]:text-txt-accent">
                        <span>{period?.number}</span>
                      </div>
                      <div className="flex gap-2 text-sm font-normal text-txt-secondary">
                        <span>
                          {formatDate(new Date(period?.start), 'd MMM')}
                        </span>
                        <span>—</span>
                        <span>
                          {formatDate(new Date(period?.end), 'd MMM yyyy')}
                        </span>
                      </div>
                    </div>
                    {vaultId && (
                      <p className="text-txt-secondary">
                        Opening Balance: {formatter(vaultOpeningBalance)}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button variant="secondary" iconOnly onClick={handleDock}>
                <PanelBottom />
              </Button>
              <DialogClose onClick={() => closeToOrigin()}>
                <XMarkIcon />
              </DialogClose>
            </div>
          </DialogHeader>

          {/* <Separator /> */}
          <DialogBody className="flex flex-col gap-4">
            {isLoading ? (
              <div className="flex w-full flex-col gap-4">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : (
              <TransactionListTable />
            )}
          </DialogBody>
          <p className="px-4 text-right text-txt-secondary">
            {vaultId && (
              <>
                <p>Closing Balance: {formatter(vaultClosingBalance)}</p>
              </>
            )}
          </p>
          {/* <Separator /> */}
          <DialogFooter className="flex items-center">
            {vaultId && (
              <>
                <Link
                  to={{
                    pathname: 'new',
                    search: retrieveSearchParams('in'),
                  }}
                >
                  <Button type="button" variant="default" className="">
                    <PlusCircleIcon /> <ButtonText> Money In</ButtonText>
                  </Button>
                </Link>
                <Link
                  to={{
                    pathname: 'new',
                    search: retrieveSearchParams('out'),
                  }}
                >
                  <Button type="button" variant="default">
                    <PlusCircleIcon />
                    <ButtonText>Money Out</ButtonText>
                  </Button>
                </Link>

                <Link
                  to={{
                    pathname: 'new',
                    search: retrieveSearchParams('transfer'),
                  }}
                >
                  <Button type="button" variant="default">
                    <PlusCircleIcon />
                    <ButtonText>Transfer</ButtonText>
                  </Button>
                </Link>
              </>
            )}

            {categoryId && (
              <Link
                to={{
                  pathname: 'new',
                  search: retrieveSearchParams(
                    categoryEntity?.direction === 1 ? 'in' : 'out',
                  ),
                }}
              >
                <Button type="button" variant="default">
                  <PlusCircleIcon />
                  <ButtonText>Add Transaction</ButtonText>
                </Button>
              </Link>
            )}

            <Button
              type="button"
              variant="secondary"
              onClick={() => closeToOrigin()}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* <div
        className="relative my-3 w-[320px]"
        id="portal-right"
        key={`${location.pathname}-${location.search}`}
      >
        <Outlet key={`${location.pathname}-${location.search}`} />
      </div> */}
      {showRightPanel && (
        <Portal container={document.getElementById('main-cont')} asChild>
          <AnimatePresence>
            <motion.div
              initial={{ width: '0', overflow: 'hidden', minWidth: '0' }}
              animate={{ width: '320px', overflow: 'unset', minWidth: '320px' }}
              transition={{ duration: 0.2 }}
              exit={{
                width: '0',
                minWidth: '0',
                transition: { duration: 0.1 },
              }}
              id="portal-right"
              className="relative my-3"
            >
              <Outlet key={`${location.pathname}-${location.search}`} />
            </motion.div>
          </AnimatePresence>
        </Portal>
      )}
    </>
  )
}
